/**
 * Client-side size ordering — mirrors server/catalog/normalizers/size.js
 * so "US 5" sorts before "US 10" (not lexicographically).
 */

const ALPHA_SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL', 'OS'];

function sizeSortKey(size) {
  const raw = String(size || '').trim();
  const upper = raw.toUpperCase().replace(/\s+/g, ' ');
  const alphaIdx = ALPHA_SIZE_ORDER.indexOf(upper);
  if (alphaIdx !== -1) return { kind: 0, n: alphaIdx, label: upper };

  const prefixed = upper.match(/^(AU|US|UK|EU)\s*(\d{1,2}(?:\.\d)?)$/);
  if (prefixed) {
    const prefixRank = { AU: 0, US: 1, UK: 2, EU: 3 }[prefixed[1]] ?? 9;
    return { kind: 1, n: parseFloat(prefixed[2]), p: prefixRank, label: upper };
  }

  if (/^\d{1,2}(\.\d)?$/.test(upper)) {
    return { kind: 1, n: parseFloat(upper), p: 1, label: upper };
  }

  const embedded = upper.match(/(\d{1,2}(?:\.\d)?)/);
  if (embedded) {
    return { kind: 1, n: parseFloat(embedded[1]), p: 5, label: upper };
  }

  return { kind: 2, n: 0, p: 0, label: upper };
}

function compareSizeKeys(ka, kb) {
  if (ka.kind !== kb.kind) return ka.kind - kb.kind;
  if (ka.kind === 1) {
    if (ka.n !== kb.n) return ka.n - kb.n;
    if ((ka.p || 0) !== (kb.p || 0)) return (ka.p || 0) - (kb.p || 0);
  } else if (ka.kind === 0 && ka.n !== kb.n) {
    return ka.n - kb.n;
  }
  return String(ka.label).localeCompare(String(kb.label));
}

export function sortSizes(sizes = []) {
  return Array.from(new Set(sizes.filter(Boolean).map((s) => String(s).trim()))).sort((a, b) =>
    compareSizeKeys(sizeSortKey(a), sizeSortKey(b))
  );
}

/** Prefer stock rows when present; otherwise fall back to sizes list. */
export function isSizeInStock(productOrOffer, size) {
  if (!productOrOffer || size == null) return false;
  const target = String(size).trim().toUpperCase();
  const stock = productOrOffer.retailerSizeStock?.length
    ? productOrOffer.retailerSizeStock
    : (productOrOffer.stock || []);

  if (stock.length > 0) {
    const rows = stock.filter((s) => String(s.size || '').trim().toUpperCase() === target);
    if (rows.length === 0) return false;
    return rows.some(
      (s) => s.available === true || (s.available !== false && Number(s.quantity) > 0)
    );
  }

  const displaySizes = productOrOffer.retailerSizes?.length
    ? productOrOffer.retailerSizes
    : (productOrOffer.sizes || productOrOffer.availableSizes || []);
  return displaySizes.some(
    (s) => String(s).trim().toUpperCase() === target
  );
}

export function collectDisplaySizes(offers = []) {
  const labels = offers.flatMap((o) => [
    ...(o.retailerSizes?.length ? o.retailerSizes : (o.sizes || o.availableSizes || [])),
    ...((o.retailerSizeStock?.length ? o.retailerSizeStock : (o.stock || [])).map((s) => s.size)),
  ]);
  return sortSizes(labels);
}
